import { mockEndpoints as ep } from './mockEndpoints';
import { createLiveApi, type LiveApiOptions } from './liveApi';
import type { HttpClient } from './http';
import type {
  ChangePlan, HomeFeed, CommandState, CreateSosRequest, CreateSupportRequest, CreateTripRequest, Id, LoginRequest,
  LoginResponse, NotificationItem, Page, PlanDecisionRequest, Place, Quote, QuoteRequest, SupportTicket,
  TimelineEntry, Trip, TripSummary, User, AuthTokens,
} from './types';

export interface CommandRef { status: number; commandId: Id; tripId?: Id }

/** Lớp gọi API có kiểu. Mọi lệnh ghi bắt buộc truyền idempotencyKey. */
export function createMockApi(http: HttpClient) {
  const get = async <T>(path: string, query?: Record<string, string | number | undefined>, signal?: AbortSignal) =>
    (await http.request<T>(path, { query, signal })).data;

  return {
    auth: {
      login: async (body: LoginRequest) => (await http.request<LoginResponse>(ep.auth.login, { method: 'POST', body, auth: false })).data,
      refresh: async (refreshToken: string): Promise<AuthTokens | undefined> =>
        (await http.request<AuthTokens>(ep.auth.refresh, { method: 'POST', body: { refreshToken }, auth: false })).data,
      me: (signal?: AbortSignal) => get<User>(ep.auth.me, undefined, signal),
      logout: async (refreshToken: string) => {
        await http.request(ep.auth.logout, { method: 'POST', body: { refreshToken }, auth: false });
      },
    },
    places: {
      search: (q: string, signal?: AbortSignal) => get<Place[]>(ep.places.search, { q }, signal),
    },
    quotes: {
      create: async (body: QuoteRequest, signal?: AbortSignal) =>
        (await http.request<Quote>(ep.quotes.create, { method: 'POST', body, signal })).data,
    },
    trips: {
      /** 202 ⇒ chỉ là "đã nhận yêu cầu"; phải theo dõi command. */
      create: async (body: CreateTripRequest, idempotencyKey: string): Promise<CommandRef> => {
        const r = await http.request<{ commandId: Id; tripId?: Id }>(ep.trips.create, { method: 'POST', body, idempotencyKey });
        return { status: r.status, commandId: r.data.commandId, tripId: r.data.tripId };
      },
      list: (p: { cursor?: string; state?: string; limit?: number }, signal?: AbortSignal) =>
        get<Page<TripSummary>>(ep.trips.list, { cursor: p.cursor, state: p.state, limit: p.limit }, signal),
      detail: (id: Id, signal?: AbortSignal) => get<Trip>(ep.trips.detail(id), undefined, signal),
      timeline: (id: Id, signal?: AbortSignal) => get<TimelineEntry[]>(ep.trips.timeline(id), undefined, signal),
      cancel: async (id: Id, idempotencyKey: string) =>
        (await http.request<{ commandId?: Id; status: string }>(ep.trips.cancel(id), { method: 'POST', idempotencyKey })),
      confirm: async (id: Id, step: 'pickup' | 'complete', idempotencyKey: string) =>
        (await http.request<{ status: string }>(ep.trips.confirm(id, step), { method: 'POST', idempotencyKey })),
      plans: (id: Id, signal?: AbortSignal) => get<ChangePlan[]>(ep.trips.plans(id), undefined, signal),
    },
    commands: {
      detail: (id: Id, signal?: AbortSignal) => get<CommandState>(ep.commands.detail(id), undefined, signal),
    },
    plans: {
      detail: (id: Id, signal?: AbortSignal) => get<ChangePlan>(ep.plans.detail(id), undefined, signal),
      consent: async (id: Id, body: PlanDecisionRequest, idempotencyKey: string) =>
        (await http.request<{ commandId?: Id }>(ep.plans.consent(id), { method: 'POST', body, idempotencyKey })).data,
      reject: async (id: Id, body: PlanDecisionRequest, idempotencyKey: string) => {
        await http.request(ep.plans.reject(id), { method: 'POST', body, idempotencyKey });
      },
      refresh: async (id: Id) => (await http.request<ChangePlan>(ep.plans.refresh(id), { method: 'POST' })).data,
      withdraw: async (id: Id, body: PlanDecisionRequest, idempotencyKey: string) =>
        (await http.request<{ commandId?: Id }>(ep.plans.withdraw(id), { method: 'POST', body, idempotencyKey })).data,
    },
    support: {
      sos: async (body: CreateSosRequest, idempotencyKey: string) =>
        (await http.request<SupportTicket>(ep.support.sos, { method: 'POST', body, idempotencyKey })).data,
      createRequest: async (body: CreateSupportRequest, idempotencyKey: string) =>
        (await http.request<SupportTicket>(ep.support.requests, { method: 'POST', body, idempotencyKey })).data,
      list: (signal?: AbortSignal) => get<SupportTicket[]>(ep.support.requests, undefined, signal),
      detail: (id: Id, signal?: AbortSignal) => get<SupportTicket>(ep.support.detail(id), undefined, signal),
    },
    home: {
      feed: (signal?: AbortSignal) => get<HomeFeed>(ep.home.feed, undefined, signal),
    },
    notifications: {
      list: (p: { cursor?: string; category?: string }, signal?: AbortSignal) =>
        get<Page<NotificationItem>>(ep.notifications.list, { cursor: p.cursor, category: p.category }, signal),
      markSeen: async (id: Id) => {
        await http.request(ep.notifications.seen(id), { method: 'POST' }); // idempotent theo thiết kế: gửi lại an toàn
      },
    },
  };
}

export type Api = ReturnType<typeof createMockApi>;

export function createApi(http: HttpClient, options?: LiveApiOptions): Api {
  return options?.mode === 'live' ? createLiveApi(http, options) : createMockApi(http);
}
